import type { Ref } from 'vue'
import { nextTick } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useScroll, useTitle } from '@vueuse/core'
import { $loadTextFromCDN } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, CHARGE_TYPE } from '@/constants'
import { useUserStore } from '@/stores'

/**
 * 章节加载逻辑 Composable
 * 负责加载章节内容、处理解锁逻辑等
 */

interface ChapterLoaderOptions {
  contentId: Ref<string>
  chapterId: Ref<string>
  book: Ref<any>
  chapter: Ref<any>
  content: Ref<string>
  chapterList: Ref<any[]>
  containerRef: Ref<HTMLElement | undefined>
  skeleton: Ref<boolean>
  onChapterVisitTrack: (contentId: string, chapterId: string, sequenceNo: number, chapterVersionId: string) => void
  onReportScrollLocation: (y: number) => void
  onErrorHandle: () => void
}

export function useChapterLoader(options: ChapterLoaderOptions) {
  const {
    contentId,
    chapterId,
    book,
    chapter,
    content,
    chapterList,
    containerRef,
    skeleton,
    onChapterVisitTrack,
    onReportScrollLocation,
    onErrorHandle,
  } = options

  const router = useRouter()
  const userStore = useUserStore()
  const title = useTitle('Loading...')

  /**
   * 判断是否应该自动解锁章节
   */
  function shouldAutoUnlock(chapterItem: any): boolean {
    if (!userStore.$state.autoLock)
      return false

    // 单章付费逻辑
    if (book.value?.chargeType === CHARGE_TYPE.CHAPTER)
      return chapterItem.chapterPrice < userStore.$state.coinBalance

    // 整本付费逻辑
    if (book.value?.chargeType === CHARGE_TYPE.CONTENT)
      return book.value?.priceAmount < userStore.$state.coinBalance

    return false
  }

  /**
   * 加载已解锁章节
   */
  async function loadUnlockedChapter(chapterItem: any) {
    try {
      onChapterVisitTrack(contentId.value, chapterId.value, chapterItem.sequenceNo, chapterItem.chapterVersionId)
      content.value = await $loadTextFromCDN(chapter.value.bookChapterFileUrl)
      skeleton.value = false
    }
    catch (error) {
      onErrorHandle()
    }
  }

  /**
   * 加载锁定章节的免费部分
   */
  async function loadLockedChapterFreeContent() {
    try {
      content.value = await $loadTextFromCDN(chapter.value.bookChapterFreeFileUrl)
      skeleton.value = false
    }
    catch (error) {
      onErrorHandle()
    }
  }

  /**
   * 获取并解锁章节
   */
  async function fetchChapter(resetReadTime?: () => void) {
    try {
      const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
        contentId: contentId.value,
        contentChapterId: chapterId.value,
        sequenceNo: 1,
      })

      // 更新章节列表中的锁定状态和文件URL
      const chapterItem = chapterList.value.find((item: any) => item.id === chapterId.value)
      if (chapterItem) {
        chapterItem.lockStatus = CHAPTER_LOCK.UNLOCK
        chapterItem.sequenceNo = data.sequenceNo
        chapterItem.bookChapterFileUrl = data.bookChapterFileUrl
      }

      // 更新当前章节状态
      chapter.value.lockStatus = CHAPTER_LOCK.UNLOCK
      chapter.value.sequenceNo = data.sequenceNo
      chapter.value.bookChapterFileUrl = data.bookChapterFileUrl

      // 加载章节内容
      content.value = await $loadTextFromCDN(data.bookChapterFileUrl)

      // 更新标题
      title.value = chapter.value?.contentChapterName

      // 滚动到指定位置
      const { y } = useScroll(containerRef)
      const scrollY = Number(router.currentRoute.query.scrollY ?? 0)
      nextTick(() => {
        y.value = scrollY
      })

      // 上报
      nextTick(() => {
        onReportScrollLocation(0)
        onChapterVisitTrack(contentId.value, chapterId.value, chapterItem.sequenceNo, chapterItem.chapterVersionId)
      })

      // 刷新用户信息
      await userStore.init({ force: true })
    }
    catch (error: any) {
      const { code } = error
      // 购买畅读会员, 但是阅读本数达到上限
      if (code === 406)
        return

      // 403 为未购买章节, 其他错误需要处理
      if (code !== 403)
        onErrorHandle()
    }
    finally {
      resetReadTime?.()
      skeleton.value = false
    }
  }

  /**
   * 装载章节 (核心逻辑)
   */
  async function loadChapter() {
    // 查找章节信息
    const chapterItem = chapterList.value.find((item: any) => item.id === chapterId.value)
    if (!chapterItem)
      return

    // 更新标题
    useTitle(chapterItem.chapterName)

    // 构建章节数据
    chapter.value = {
      chargeType: book.value?.chargeType,
      contentChapterName: chapterItem.chapterName,
      priceAmount: book.value?.chargeType === CHARGE_TYPE.CHAPTER ? chapterItem.chapterPrice : book.value?.priceAmount,
      memberCoin: userStore.$state.coinBalance,
      lockStatus: chapterItem.lockStatus,
      feeType: chapterItem.feeType,
      bookChapterFileUrl: chapterItem.bookChapterFileUrl,
      bookChapterFreeFileUrl: chapterItem.bookChapterFreeFileUrl,
      sequenceNo: chapterItem.sequenceNo,
      prev: chapterItem.preId ? { contentChapterId: chapterItem.preId } : null,
      next: chapterItem.nextId ? { contentChapterId: chapterItem.nextId } : null,
      chapterVersionId: chapterItem.chapterVersionId,
    }

    // 章节已解锁 - 直接加载完整内容
    if (chapterItem.lockStatus !== CHAPTER_LOCK.LOCK)
      await loadUnlockedChapter(chapterItem)

    // 章节锁定 - 判断是否自动解锁
    else if (shouldAutoUnlock(chapterItem))
      await fetchChapter()

    // 章节锁定 - 加载免费部分
    else
      await loadLockedChapterFreeContent()

    // 重置滚动位置
    const { y } = useScroll(containerRef)
    nextTick(() => {
      y.value = 0
    })
    nextTick(() => {
      onReportScrollLocation(0)
    })
  }

  /**
   * 获取最后的阅读位置
   */
  async function fetchLatestPosition(): Promise<boolean> {
    const { data } = await api.post<any, PostReadPosResponse>('/read/pos', { contentId: book.value!.id })

    router.replace({
      name: 'chapter',
      params: { contentId: book.value!.id, chapterId: data.contentChapterId },
      query: {
        scrollY: String(data.sectionMediaPlayedSeconds),
        isShowpopupRecharge: router.currentRoute.query.isShowpopupRecharge,
        recommend: router.currentRoute.query.recommend,
      },
    })

    return chapterId.value !== data.contentChapterId
  }

  return {
    loadChapter,
    fetchChapter,
    fetchLatestPosition,
  }
}
